import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGameDataContext } from "@context/GameDataContext";
import { Alert, Box } from "@mui/material";
import ChallengeDetailPageHeader from "../components/challenge-detail-page/ChallengeDetailPageHeader";
import EditChallenge from "../components/challenge-detail-page/EditChallenge";
import Grid from "@mui/material/Grid";
import ChallengeDetailPageFooter from "../components/challenge-detail-page/ChallengeDetailPageFooter";
import ChallengeList from "../components/challenge-detail-page/challengeList/ChallengeList";
import ChallengeTabletActionButtons from "../components/challenge-detail-page/footer/ChallengeTabletActionButtons";
import { useResponsiveContext } from "@context/ResponsiveContext";
import SelectedChallengeContextProvider from "../context/SelectedChallengeContext";
import GameEventLog from "../components/event-history/GameEventLog";
import { SamsScope, samsScopes } from "@/models/samsVbl";
import { useAppDataContext } from "@context/AppContext";

const GameChallengeView: FC = () => {
  let { samsId, scope } = useParams();
  const { game, gameLoadingState, useGame } = useGameDataContext();
  const { challengeTickerLocation } = useAppDataContext();
  const { onTablet } = useResponsiveContext();

  const [sidePanelDisplay, setSidePanelDisplay] = useState<"block" | "none">(
    "block",
  );
  const [editChallengeSize, setEditChallengeSize] = useState<5 | 3>(3);
  const [
    showChallengeTabletActionButtons,
    setShowChallengeTabletActionButtons,
  ] = useState(false);

  useEffect(() => {
    if (samsId && samsScopes.includes(scope as SamsScope)) {
      if (!game || game.samsId !== samsId) {
        useGame(samsId, scope as SamsScope);
      } else {
        console.debug("SamsId already correctly set", samsId, game.samsId);
      }
    } else {
      console.error("NO SAMS ID");
    }
  }, [samsId, game, scope]);

  useEffect(() => {
    setSidePanelDisplay(onTablet ? "none" : "block");
    setEditChallengeSize(onTablet ? 5 : 3);
    setShowChallengeTabletActionButtons(onTablet);
  }, [onTablet]);

  if (!samsScopes.includes(scope as SamsScope)) {
    return <Alert severity={"error"}>Scope is invalid</Alert>;
  } else if (!game) {
    if (gameLoadingState === "loading") {
      return <Alert severity="info">Das Spiel wird geladen...</Alert>;
    } else {
      return (
        <Alert severity="warning">
          Es konnte kein Spiel mit ID {samsId} gefunden werden!
        </Alert>
      );
    }
  } else {
    return (
      <SelectedChallengeContextProvider>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            overflow: "clip",
          }}
        >
          <ChallengeDetailPageHeader />
          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              minHeight: "2em",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Grid
              container
              columns={5}
              width={"1400px"}
              overflow={"hidden"}
              flexDirection={
                challengeTickerLocation === "right" ? "row" : "row-reverse"
              }
            >
              <Grid
                size={1}
                maxHeight={"100%"}
                display={sidePanelDisplay}
                overflow={"hidden"}
              >
                <ChallengeList />
              </Grid>
              <Grid
                size={editChallengeSize}
                maxHeight={"100%"}
                overflow={"hidden"}
              >
                <EditChallenge />
              </Grid>
              <Grid size={1} maxHeight={"100%"} display={sidePanelDisplay}>
                <GameEventLog variant={"narrow"} />
              </Grid>
            </Grid>
          </Box>
          <ChallengeDetailPageFooter />
          {showChallengeTabletActionButtons && <ChallengeTabletActionButtons />}
        </Box>
      </SelectedChallengeContextProvider>
    );
  }
};

export default GameChallengeView;
